"use client";

import Image from "next/image";
import { renderRichText } from "@storyblok/react";
import { RichText } from "@/components/typography/RichText";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";

export type ArticlePreviewData = {
  title: string;
  subtitle?: string;
  excerpt?: string;
  /** Richtext, truncated the same way the article page truncates for non-members. */
  previewText?: any;
  image?: { src: string; alt: string };
  tags?: string[];
  memberOnly?: boolean;
};

type Props = {
  article: ArticlePreviewData | null;
  onClose: () => void;
};

export function ArticlePreviewModal({ article, onClose }: Props) {
  if (!article) return null;

  const previewHtml = article.previewText
    ? renderRichText(article.previewText)
    : "";

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg p-0 block max-h-[85vh] overflow-y-auto overscroll-contain rounded-lg bg-sand-10 border-0 gap-0" closeClassName="rounded-full bg-sand-10 p-1.5 text-black opacity-100 hover:bg-sand-25 shadow-sm">
        {article.image && (
          <div className="relative w-full aspect-[16/9] overflow-hidden">
            <Image
              src={article.image.src}
              alt={article.image.alt}
              fill
              className="object-cover"
            />
          </div>
        )}

        <div className="p-6 space-y-3">
          {article.tags && article.tags.length > 0 && (
            <div className="flex flex-wrap [&>*+*]:ml-2">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="text-label text-gray-75 bg-sand-25 px-2 py-0.5 rounded-full"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}

          <DialogTitle className="text-headline-md">
            {article.title}
          </DialogTitle>

          {article.subtitle && (
            <p className="text-copy text-black-80">{article.subtitle}</p>
          )}

          {/* Prefer the truncated article body — it is the same slice the
              article page shows a non-member, so the preview matches what
              follows. The plain excerpt is the fallback. */}
          {previewHtml ? (
            <RichText html={previewHtml} className="text-copy" />
          ) : (
            article.excerpt && (
              <p className="text-copy text-gray-75">{article.excerpt}</p>
            )
          )}

          {article.memberOnly && (
            <p className="text-label text-sky-100 mt-2">Member-Inhalt</p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
